<template>
    <ButtonWrapper>
        <template #buttons>
            <div v-if="!inError">
                <a
                    v-for="t in toggles"
                    :key="t.id"
                    :title="`${
                        typeof t.config.tooltip === 'undefined'
                            ? null
                            : t.config.tooltip
                    }`"
                    @click.prevent="onToggleClick(t.id)"
                >
                    <i :class="`fa-lg ${t.config.iconClass}`"></i>
                </a>
            </div>
            <div
                :id="'widgetMenuWrapper' + widgetId"
                v-click-outside="hideMenu"
                class="vue-widget-menu-wrapper"
            >
                <a
                    v-if="!inError"
                    :title="$t('menu')"
                    @click.prevent="showMenu = !showMenu"
                >
                    <i class="fa-lg fa-solid fa-bars"></i>
                </a>
                <div
                    v-show="showMenu"
                    :id="'widgetMenu' + widgetId"
                    class="vue-widget-menu"
                >
                    <button v-if="canMinimise" @click.prevent="onMinimiseClick">
                        {{ $t("minimise") }}
                    </button>
                    <button v-if="canMaximise" @click.prevent="onMaximiseClick">
                        {{ $t("maximise") }}
                    </button>
                    <button v-if="canRestore" @click.prevent="onRestoreClick">
                        {{ $t("restore") }}
                    </button>
                    <button @click.prevent="onDeleteClick">
                        {{ $t("delete") }}
                    </button>
                    <hr v-show="canOpenInNewWindow" />
                    <button v-show="canPrint" @click.prevent="onPrintClick">
                        {{ $t("print") }}
                    </button>
                    <button v-show="canExport" @click.prevent="onExportClick">
                        {{ $t("export") }}
                    </button>
                    <hr
                        v-show="
                            canPauseUpdates ||
                            canResumeUpdates ||
                            !isSettingsOpen
                        "
                    />
                    <button
                        v-show="canPauseUpdates"
                        @click.prevent="onPauseClick"
                    >
                        {{ $t("pause") }}
                    </button>
                    <button
                        v-show="canResumeUpdates"
                        @click.prevent="onResumeClick"
                    >
                        {{ $t("resume") }}
                    </button>
                    <button
                        v-if="canOpenSettings"
                        class="vue-icon vue-icon-settings"
                        @click.prevent="onSettingsOpenClick"
                    >
                        {{ $t("settings") }}
                    </button>
                    <hr v-if="widgetHasHelp" />
                    <button
                        v-if="widgetHasHelp"
                        class="vue-icon vue-icon-help"
                        @click.prevent="onHelpClick"
                    >
                        {{ $t("help") }}
                    </button>
                </div>
            </div>
            <ButtonClose @clicked="onDeleteClick" />
        </template>
    </ButtonWrapper>
</template>

<script>
import vClickOutside from "v-click-outside";
import ButtonClose from "../buttons/ButtonClose.vue";
import ButtonWrapper from "../buttons/ButtonWrapper.vue";
import * as HorizonEvents from "../../constants/horizonEvents";
import * as HorizonTasks from "../../constants/horizonTasks";
import * as WidgetStates from "../../constants/widgetStates";
import * as ModalTypes from "../../constants/modalTypes";
import * as WidgetDisplayStates from "../../constants/widgetDisplayStates";
import * as WidgetEvents from "../../constants/widgetEvents";
import * as HorizonRoleRights from "../../constants/horizonRoleRights";
import * as PageTypes from "../../constants/pageTypes";
import * as EnglishStrings from "../../localisation/components/widgets/widgetHeaderButtons.en.json";
import * as FrenchStrings from "../../localisation/components/widgets/widgetHeaderButtons.fr.json";
import { horizonService } from "../../stateMachines/horizonStateMachine";

export default {
    name: "WidgetHeaderButtons",
    components: {
        ButtonClose,
        ButtonWrapper,
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    props: {
        widgetId: {
            type: Number,
            required: true,
        },
    },
    emits: ["save-location-info"],
    data() {
        return {
            showMenu: false,
        };
    },
    computed: {
        currentState() {
            return horizonService.context.widgets[this.widgetId]._state.value;
        },
        isPaused() {
            return this.currentState === WidgetStates.PAUSED;
        },
        inError() {
            return this.currentState === WidgetStates.ERRORED;
        },
        isSettingsOpen() {
            return this.currentState === WidgetStates.SETTINGS_OPEN;
        },
        widgetHasSettings() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .hasSettings;
        },
        displayState() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .displayMachine.state.value;
        },
        isDisplayNormal() {
            return this.displayState === WidgetDisplayStates.NORMAL;
        },
        isMinimised() {
            return this.displayState === WidgetDisplayStates.MINIMISED;
        },
        isMaximised() {
            return this.displayState === WidgetDisplayStates.MAXIMISED;
        },
        userCanPrint() {
            return horizonService.checkRoleRight(
                HorizonRoleRights.WIDGET_PRINT
            );
        },
        userCanExport() {
            return horizonService.checkRoleRight(
                HorizonRoleRights.WIDGET_EXPORT
            );
        },
        userCanPause() {
            return horizonService.checkRoleRight(
                HorizonRoleRights.WIDGET_FREEZE
            );
        },
        widgetCanPrint() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .widgetData === null
                ? false
                : horizonService.context.widgets[this.widgetId]._state.context
                      .widgetData.canPrint === true;
        },
        widgetCanExport() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .widgetData === null
                ? false
                : horizonService.context.widgets[this.widgetId]._state.context
                      .widgetData.canExport === true;
        },
        widgetCanPause() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .widgetData === null
                ? false
                : horizonService.context.widgets[this.widgetId]._state.context
                      .widgetData.canPauseUpdates === true;
        },
        canMinimise() {
            return !this.isSettingsOpen && this.isDisplayNormal;
        },
        canMaximise() {
            return (
                !this.isSettingsOpen &&
                this.isDisplayNormal &&
                horizonService.context.widgets[this.widgetId]._state.context
                    .containerData.size.canMax
            );
        },
        canRestore() {
            return (
                !this.isSettingsOpen && (this.isMinimised || this.isMaximised)
            );
        },
        canPrint() {
            return (
                this.isWidgetContentVisible &&
                this.userCanPrint &&
                this.widgetCanPrint
            );
        },
        canExport() {
            return (
                this.isWidgetContentVisible &&
                this.userCanExport &&
                this.widgetCanExport
            );
        },
        canOpenInNewWindow() {
            return this.canExport || this.canPrint;
        },
        canPauseUpdates() {
            return (
                this.isWidgetContentVisible &&
                this.userCanPause &&
                this.widgetCanPause &&
                !this.isPaused
            );
        },
        canOpenSettings() {
            return this.widgetHasSettings && this.isWidgetContentVisible;
        },
        isWidgetContentVisible() {
            return !this.isSettingsOpen && !this.isMinimised;
        },
        canResumeUpdates() {
            return !this.isSettingsOpen && !this.isMinimised && this.isPaused;
        },
        widgetHasHelp() {
            return this.helpUrl !== null && this.helpUrl !== "";
        },
        helpUrl() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .containerData.helpUrl;
        },
        toggles() {
            return horizonService.context.widgets[this.widgetId]._state.context
                .toggles;
        },
        pageType() {
            const pageType =
                horizonService.context.widgets[this.widgetId]._state.context
                    .containerData.location.pageType;
            if (pageType === undefined) {
                return PageTypes.HORIZON_BASIC;
            }
            return PageTypes.TRANSIENT;
        },
    },
    methods: {
        hideMenu() {
            this.showMenu = false;
        },
        onSettingsOpenClick() {
            this.hideMenu();
            horizonService.sendToWidget(
                this.widgetId,
                WidgetEvents.SETTINGS_OPEN
            );
        },
        onMaximiseClick() {
            this.hideMenu();
            this.$emit("save-location-info");
            horizonService.sendToWidget(
                this.widgetId,
                WidgetEvents.DISPLAY_MAXIMISING
            );
        },
        onMinimiseClick() {
            this.hideMenu();
            horizonService.sendToWidget(
                this.widgetId,
                WidgetEvents.DISPLAY_MINIMISING
            );
        },
        onRestoreClick() {
            this.hideMenu();
            horizonService.sendToWidget(
                this.widgetId,
                WidgetEvents.DISPLAY_RESTORE
            );
        },
        onDeleteClick() {
            this.hideMenu();
            if (this.pageType === PageTypes.TRANSIENT) {
                window.close();
                return;
            }
            horizonService.service.send({
                type: HorizonEvents.QUEUE_ADD,
                task: {
                    type: HorizonTasks.SHOW_MODAL,
                    data: {
                        widgetId: this.widgetId,
                        modalType: ModalTypes.DELETE_WIDGET,
                    },
                },
            });
        },
        onPrintClick() {
            this.hideMenu();
            window.open(`/Display?q=${btoa(`id=${this.widgetId}&p=1`)}`);
        },
        onExportClick() {
            this.hideMenu();
            window.open(`/Display?q=${btoa(`id=${this.widgetId}&e=1`)}`);
        },
        onPauseClick() {
            this.hideMenu();
            horizonService.sendToWidget(
                this.widgetId,
                WidgetEvents.PAUSE_UPDATES
            );
        },
        onResumeClick() {
            this.hideMenu();
            horizonService.sendToWidget(
                this.widgetId,
                WidgetEvents.RESUME_UPDATES
            );
        },
        onHelpClick() {
            this.hideMenu();
            horizonService.service.send({
                type: HorizonEvents.QUEUE_ADD,
                task: {
                    type: HorizonTasks.SHOW_HELP,
                    helpUrl: this.helpUrl,
                },
            });
        },
        onToggleClick(toggleId) {
            horizonService.sendToWidget(this.widgetId, {
                type: WidgetEvents.TOGGLE,
                id: toggleId,
            });
        },
    },
    i18n: {
        messages: {
            en: EnglishStrings,
            fr: FrenchStrings,
        },
    },
};
</script>

<style lang="scss" scoped>
.vue-widget-menu-wrapper {
    display: inline-block;
}
.vue-icon {
    background-position: 6px center;
    background-repeat: no-repeat;
    line-height: 20px;
}

.vue-icon-settings {
    /* webpackIgnore: true */
    background-image: url(/dist/images/WidgetMenu/page_white_edit.png);
}

.vue-icon-help {
    /* webpackIgnore: true */
    background-image: url(/dist/images/WidgetMenu/information.png);
}

.vue-widget-menu {
    background-color: var(--colour-context-menu-background);
    border: 1px solid var(--colour-context-menu-border);
    cursor: pointer;
    position: absolute;
    width: 150px;
    right: 0px;
    z-index: 99999;

    hr {
        border-top: 1px solid var(--colour-context-menu-border);
        margin: 0px;
    }

    button {
        box-shadow: none;
        border: none;
        display: block;
        line-height: 20px;
        padding: 1px 5px 1px 28px;
        text-align: left;
        width: 100%;

        &:hover {
            background-color: var(--colour-context-menu-hover);
            color: var(--colour-site-background-one);
        }

        & > * {
            display: inline-block;
        }
    }
}
</style>
